import { useTranslation } from 'react-i18next';
import { cn } from '@/core/lib/utils';
import { FIELD_TYPE } from '../types';
import type { FieldType } from '../types';
import { getFieldRegistryEntry, resolveFieldType } from '../registry/field-registry';

interface FieldTypeBadgeProps {
  /** Numeric FieldType enum value */
  fieldType?: FieldType;
  /** String fieldType from API response — resolved automatically */
  fieldTypeStr?: string | null;
  size?: 'sm' | 'md';
  className?: string;
}

export function FieldTypeBadge({
  fieldType,
  fieldTypeStr,
  size = 'md',
  className,
}: FieldTypeBadgeProps) {
  const { t } = useTranslation('customFields');

  const resolvedType: FieldType | null =
    fieldType !== undefined ? fieldType : resolveFieldType(fieldTypeStr ?? null);

  if (resolvedType === null) {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-600',
          className,
        )}
      >
        {t('fieldTypes.text')}
      </span>
    );
  }

  const entry = getFieldRegistryEntry(resolvedType);
  const Icon = entry.icon;
  const iconSize = size === 'sm' ? 'h-3 w-3' : 'h-4 w-4';

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium',
        entry.colorClass,
        className,
      )}
    >
      <Icon className={iconSize} aria-hidden="true" />
      {size === 'md' && <span>{t(entry.labelKey.replace('customFields.', ''))}</span>}
    </span>
  );
}

/** Returns a FieldType number from the FIELD_TYPE const object by name string. */
export function fieldTypeFromString(name: string): FieldType {
  const key = name as keyof typeof FIELD_TYPE;
  return FIELD_TYPE[key] ?? FIELD_TYPE.Text;
}
